import { resolveSeatTier, type PerSeatTier } from "@/components/pricing/clinic-seat-pricing";

import { applyBuyerPriceFactor } from "./buyer-price";

import type { VolumeTierInfo } from "../../components/billing/checkout/checkout-types";

// Legacy source: src/lib/pricing/calculate-plan-price.ts (1:1 port; only the
// VolumeTierInfo import path changed — the type now lives in checkout-types).

/**
 * One selected pack add-on at checkout time. `count` is the number of packs
 * the user picked (each pack grants `batchSize` units of the feature at
 * `batchPriceEur` per pack, charged at the same cadence as the subscription).
 */
export interface SelectedPack {
  featureKey: string;
  batchSize: number;
  batchPriceEur: number;
  count: number;
}

export interface PlanPriceInput {
  basePriceMonthly: number;
  basePriceAnnual?: number; // actual annual price from DB (respects admin discount config)
  quantity: number;
  interval: "monthly" | "annual";
  volumeTiers?: VolumeTierInfo[];
  providersIncluded?: number; // default 1
  /**
   * Pack add-ons selected in the checkout modal. Each entry's
   * `count × batchPriceEur` is added to `addonsTotal`. Sourced from the
   * plan_features DB table via GET /api/billing/plan-addons, which returns
   * `batchPriceEur` already PPP-adjusted the way checkout charges it.
   */
  packs?: SelectedPack[];
  /**
   * D2 seat pricing. Mirror of `calculatePlanPrice` in
   * `apps/api/src/routes/checkout.ts` — the server is the source of truth and
   * this preview must not disagree with it.
   *
   * 'graduated' (default, doctor plans bought for a clinic): each extra doctor
   * is priced on his own rung — 5 × 29 + 3 × 23. 'flat' (clinic plans): ONE rung
   * for the whole clinic by headcount — 8 × 15.
   */
  seatMode?: "graduated" | "flat";
  /**
   * The share of the list price this buyer pays (lib/pricing/buyer-price.ts):
   * 1/1.2 for a reverse-charge company. Applied to every unit price — base,
   * seat rungs, packs — in cents, before the totals, as the server's checkout
   * applies it; the annual ratio stays the list prices'.
   */
  priceFactor?: number;
}

export interface PlanPriceResult {
  basePlanPrice: number;
  extraProvidersCount: number;
  extraProvidersTotal: number;
  perProviderPrice: number;
  addonsTotal: number;
  monthlyTotal: number;
  intervalTotal: number;
  volumeSavings: number;
  volumeDiscountPct: number;
  activeTier: VolumeTierInfo | null;
  nextTier: VolumeTierInfo | null;
  providersToNextTier: number;
  /** 'flat' bills the seats as ONE Stripe line of `quantity` × perProviderPrice. */
  seatMode: "graduated" | "flat";
}

/**
 * Calculate plan price with volume discounts and add-ons.
 * Used client-side for instant preview and server-side as source of truth.
 * Algorithm matches VolumeCalculator in pricing-client.tsx.
 */
export function calculatePlanPrice(input: PlanPriceInput): PlanPriceResult {
  const {
    basePriceAnnual,
    quantity,
    interval,
    volumeTiers = [],
    packs = [],
    seatMode = "graduated",
  } = input;
  // The tiers stay the caller's objects (`activeTier` is compared by identity);
  // every price read from them goes through `net`.
  const factor = input.priceFactor ?? 1;
  const net = (amount: number) => applyBuyerPriceFactor(amount, factor);
  const basePriceMonthly = net(input.basePriceMonthly);
  // Same rule as `includedSeatsFor` in apps/api/src/lib/pricing.ts: flat (clinic)
  // plans read the count literally, every other plan covers at least its buyer
  // (user and business plans carry 0).
  const providersIncluded =
    seatMode === "flat" ? (input.providersIncluded ?? 0) : input.providersIncluded || 1;

  // Annual ratio: how many months the annual price covers (e.g., 10 = 2 months free)
  // Derived from actual DB priceAnnual vs priceMonthly, respects admin discount config
  const annualMultiplier =
    basePriceAnnual && input.basePriceMonthly > 0 ? basePriceAnnual / input.basePriceMonthly : 10;

  // Base plan price (covers providersIncluded seats)
  const basePlanPrice = basePriceMonthly;

  // Extra providers beyond included
  const extraProvidersCount = Math.max(0, quantity - providersIncluded);

  // Find active tier for the total quantity
  const sortedTiers = [...volumeTiers].sort((a, b) => a.minProviders - b.minProviders);
  const activeTier =
    sortedTiers.find((t) => quantity >= t.minProviders && quantity <= t.maxProviders) ?? null;
  // Flat mode clamps outside the ladder instead of falling back to the plan
  // base: a 120-doctor clinic must not be quoted 120 × 99. `volumeTiers` arrive
  // already PPP-ceiled per unit (see /api/pricing/tiers convertPrice), so the
  // multiplication below keeps the API's rounding order: ceil per seat, then
  // × seats.
  const seatTiers: PerSeatTier[] = sortedTiers.map((t) => ({
    minSeats: t.minProviders,
    maxSeats: t.maxProviders,
    pricePerSeat: net(t.pricePerProvider),
  }));
  const flatSeatPrice = resolveSeatTier(seatTiers, quantity)?.pricePerSeat ?? basePriceMonthly;
  const perProviderPrice =
    seatMode === "flat"
      ? flatSeatPrice
      : activeTier
        ? net(activeTier.pricePerProvider)
        : basePriceMonthly;

  // Calculate extra providers total using volume tiers (same algorithm as VolumeCalculator)
  let extraProvidersTotal = 0;
  if (seatMode === "flat") {
    extraProvidersTotal = Math.round(perProviderPrice * extraProvidersCount * 100) / 100;
  } else {
    for (let doc = providersIncluded + 1; doc <= quantity; doc++) {
      const tier = volumeTiers.find((t) => doc >= t.minProviders && doc <= t.maxProviders);
      extraProvidersTotal += tier ? net(tier.pricePerProvider) : basePriceMonthly;
    }
  }

  // Find next tier (for "add X more to unlock Y% off" nudge)
  const activeTierIdx = activeTier ? sortedTiers.indexOf(activeTier) : -1;
  const nextTier =
    activeTierIdx >= 0 && activeTierIdx < sortedTiers.length - 1
      ? sortedTiers[activeTierIdx + 1]
      : null;
  const providersToNextTier = nextTier ? nextTier.minProviders - quantity : 0;

  // Volume savings vs all-individual pricing
  const individualTotal = quantity * basePriceMonthly;
  const volumeTotal = basePlanPrice + extraProvidersTotal;
  const volumeSavings = individualTotal - volumeTotal;
  const volumeDiscountPct =
    individualTotal > 0 ? Math.round((volumeSavings / individualTotal) * 100) : 0;

  // Pack add-ons — sum `count × batchPriceEur` per selected pack. Charged
  // at the same cadence as the subscription (monthly plan → monthly add-on,
  // annual plan → annualMultiplier × per below).
  const addonsTotal = packs.reduce(
    (sum, p) => sum + (p.count > 0 ? p.count * net(p.batchPriceEur) : 0),
    0,
  );

  // Totals
  const monthlyTotal = basePlanPrice + extraProvidersTotal + addonsTotal;
  const intervalTotal = interval === "annual" ? monthlyTotal * annualMultiplier : monthlyTotal;

  return {
    basePlanPrice,
    extraProvidersCount,
    extraProvidersTotal,
    perProviderPrice,
    addonsTotal,
    monthlyTotal,
    intervalTotal,
    volumeSavings,
    volumeDiscountPct,
    activeTier,
    nextTier,
    providersToNextTier,
    seatMode,
  };
}
